import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { extractBracketFields, applyFieldValues } from './bracketFieldUtils';
import { SYMPTOM_CODE_HINTS } from '../utils/icd10HintEngine';
import { scanNotesForConflicts, applyPronounFix, applyEndorseDeniesFix } from '../features/conflict-scanner';

const API = `${window.__WR_API_BASE__}/api`;

// Build a static code→description lookup once from the hint engine.
// Used as a fallback when the LLM drops a user-pinned code (so the displayed
// chip has at least *some* description text). The authoritative description
// lookup lives on the server; this is only for the brief render gap until
// the next generate corrects it.
const _PINNED_CODE_DESC_FALLBACK = (() => {
  const map = {};
  for (const h of SYMPTOM_CODE_HINTS) {
    if (h.preferredCode && h.preferredDesc) {
      map[h.preferredCode.toUpperCase()] = h.preferredDesc;
    }
  }
  return map;
})();

/**
 * Hook that encapsulates all document generation logic:
 * - generateDocuments / executeGeneration
 * - Bracket field detection & fill
 * - Pre-gen field prompting
 * - ICD-10 confirm/dismiss after reorder
 * - Auto-regenerate on tone change
 */
const useGeneration = ({
  // Form state
  notes, setNotes, result, setResult, isGenerating, setIsGenerating,
  setDocumentsReady, setGenerationVersion, setLastShareUrl, setActiveOutputTab,
  // Generation toggles
  generateIndication, generateClinicalNote, generateAdminLetter, generateLimitations,
  // Patient info
  patientAge, patientGender, setPatientGender, patientStatus, chiefComplaint, patientLabel, patientName: patientNameProp,
  soldierRank, soldierFirstName, soldierLastName, soldierDob, soldierUnit, soldierPhone,
  mtfName, customMtf, providerSignatureBlock,
  occupation, jobDuties,
  // Letter options
  letterType, letterContext, charLimit, includeCoverLetter, setIncludeCoverLetter,
  includePatientQuotes, includeEmAnalysis,
  apFormat,
  clinicalTone,
  // Document mode (letter / message / both)
  documentMode, messageScenario,
  encounterType, visitType, includeROS, includePE, peUnremarkable, peExceptions,
  // Medic Mode (Feb 24, 2026) — when true, output is HPI + ROS + ICD-10 handoff only.
  medicMode,
  // Prior-visit context (Feb 25, 2026) — "At Your Last Visit" merge box
  priorVisitContext,
  // v6.8.47 — Saved Medical History / Injuries (for the append-to-profile nudge).
  savedInjuries,
  // Pre-Visit Prep / RN Handoff (Feb 26, 2026) — separate field, treated as
  // handoff context by the LLM (not direct provider observation).
  prepNotes,
  // Mar 1, 2026 — Back-charting detection. When dosOverride is set AND != today's
  // date, the provider is catching up on prior days; visit-type confirmation
  // becomes critical (don't auto-trust the schedule-derived default).
  dosOverride,
  // ICD-10
  manualIcd10, liveIcd10Suggestions, primaryIcd10Codes,
  // Imaging / specialties
  imagingOrders, specialties,
  // Labs
  labOrders,
  // Meds counseling
  medsRxd, medsRxdDetail,
  // Tone
  effectiveLetterTone,
  // Template / sharing
  selectedTemplate, shareCode, updateShareSession, autoShareTeamId,
  // Feb 28, 2026 — global auto-stream broadcast support. When user has
  // `default_auto_stream = true` in profile, generated notes are silently
  // shared to ALL teams they belong to (per user direction).
  globalAutoStreamOn, userTeams,
  // Feb 28, 2026 — Format-Lock on A/P. When true AND there's a current
  // result.clinical_note (regenerate path), the payload sends
  // `prior_output_edits` + `format_lock_ap: true` so the LLM preserves
  // the provider's A/P bullet structure exactly.
  formatLockAp, currentClinicalNote,
  // Auth
  user, guestId, effectiveRole,
  // Profile
  profileSignature, profileMTF, profileRank,
  // Letterhead
  letterheadLine1, letterheadLine2, letterheadAddress,
  // Document output hooks
  showBracketModal, setShowBracketModal,
  bracketFields, setBracketFields,
  pendingGeneration, setPendingGeneration,
  showPreGenModal, setShowPreGenModal,
  preGenFields, setPreGenFields,
  // Conflict modal (Feb 26, 2026) — pre-generate input scanner
  setShowConflictModal, setConflicts,
  // Draft helpers
  currentDraftId, saveDraft,
  // Side effects
  loadHistory, trackFeature,
  // ICD-10 reorder
  setManualIcd10Order,
  // Language
  language,
  // Deferred topics
  deferredTopics,
  setDeferredTopics,
  // Auto-mark drafts
  onGenerationComplete,
}) => {
  // ICD-10 pending confirmation state
  const [icd10PendingConfirm, setIcd10PendingConfirm] = useState(false);

  // HIPAA scrubber forbid_terms — populated by the "Scrub & Refresh" action
  // on the amber HIPAA Review chip. Consumed once by the next executeGeneration
  // call, then cleared. Held in a ref so we don't add another dependency to
  // executeGeneration's already large dep list.
  const forbidTermsRef = useRef(null);

  const getFormStateForFields = useCallback(() => ({
    soldierRank, soldierFirstName, soldierLastName, soldierUnit, soldierDob,
    soldierPhone, providerSignatureBlock, mtfName, customMtf, occupation, jobDuties,
  }), [soldierRank, soldierFirstName, soldierLastName, soldierUnit, soldierDob,
       soldierPhone, providerSignatureBlock, mtfName, customMtf, occupation, jobDuties]);

  // Auto-detect deferred topic phrases from dictated notes
  const DEFERRED_PATTERNS = [
    /(?:follow[- ]?up|f\/u|fu)\s+(?:on|for|with|re:?)\s+(.+?)(?:\.|,|;|$)/gi,
    /(?:defer|deferred|next visit|next appt|next appointment|return for)\s*:?\s+(.+?)(?:\.|,|;|$)/gi,
    /(?:recheck|re-check|re check)\s+(.+?)(?:\.|,|;|$)/gi,
    /(?:will address|address at next|discuss at next|table for next)\s+(.+?)(?:\.|,|;|$)/gi,
  ];

  const extractDeferredFromNotes = useCallback((notesText) => {
    if (!notesText) return { cleanNotes: notesText, extracted: [] };
    const extracted = [];
    let cleanNotes = notesText;
    for (const pattern of DEFERRED_PATTERNS) {
      let match;
      const regex = new RegExp(pattern.source, pattern.flags);
      while ((match = regex.exec(notesText)) !== null) {
        const topic = match[1]?.trim();
        if (topic && topic.length > 3 && topic.length < 120) {
          extracted.push(topic);
        }
      }
    }
    return { cleanNotes, extracted: [...new Set(extracted)] };
  }, []);

  const handleBracketFieldsComplete = useCallback(() => {
    setShowBracketModal(false);
    setPendingGeneration(false);
    if (result?.admin_letter) {
      setResult(prev => ({ ...prev, admin_letter: applyFieldValues(prev.admin_letter, bracketFields) }));
    }
    // Feb 18, 2026: do NOT clear bracketFields. Keep them cached so a
    // "Refresh Letter" regenerate auto-applies the same values without
    // re-prompting the provider for the date and other answered fields.
    // (Previously: setBracketFields({}) here.)
  }, [result, bracketFields, setShowBracketModal, setPendingGeneration, setResult]);

  // The actual generation call
  const executeGeneration = useCallback(async () => {
    const generateTypes = [];
    const effectiveDocMode = documentMode || 'letter';

    // PATIENT-SIDE: lean output. Only generate what the patient actually asked for.
    // This prevents "I asked for a Visit Prep Script and got a full provider SOAP
    // + admin letter + script all stacked" bloat.
    if (effectiveRole === 'patient') {
      if (effectiveDocMode === 'script' || letterType === 'visit_prep_script') {
        generateTypes.push('visit_prep_script');
      } else if (effectiveDocMode === 'message') {
        generateTypes.push('portal_message');
      } else if (effectiveDocMode === 'both') {
        generateTypes.push('admin_letter', 'portal_message');
      } else {
        // default: letter mode — admin letter only, no provider-side SOAP/ICD
        generateTypes.push('admin_letter');
      }
    } else {
      // PROVIDER-SIDE: all gates honored independently (unchanged behavior)
      if (generateIndication) generateTypes.push('indication');
      if (generateClinicalNote) generateTypes.push('clinical_note');
      if (generateAdminLetter && letterType) generateTypes.push('admin_letter');
      if (generateLimitations) generateTypes.push('limitations');
      if (effectiveDocMode === 'message' || effectiveDocMode === 'both') {
        generateTypes.push('portal_message');
      }
      if (effectiveDocMode === 'script' || letterType === 'visit_prep_script') {
        generateTypes.push('visit_prep_script');
      }
    }
    const validSpecialties = specialties.filter(s => s?.trim());
    const validImagingOrders = imagingOrders.filter(i => i.type?.trim());

    if (notes.trim().length > 10 && !currentDraftId) {
      try { await saveDraft(); } catch (e) { /* continue */ }
    }

    setIsGenerating(true);
    try {
      // Auto-detect deferred topics from notes if deferred box is empty
      let effectiveDeferredTopics = deferredTopics || '';
      if (!effectiveDeferredTopics.trim()) {
        const { extracted } = extractDeferredFromNotes(notes);
        if (extracted.length > 0) {
          effectiveDeferredTopics = extracted.join('; ');
          if (setDeferredTopics) setDeferredTopics(effectiveDeferredTopics);
          toast.info(`Auto-detected deferred topics: ${extracted.join(', ')}`, { duration: 4000 });
        }
      }

      const patientFullName = [soldierRank, soldierFirstName, soldierLastName].filter(Boolean).join(' ').trim() || null;
      const patientDobFormatted = soldierDob ? new Date(soldierDob).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }) : null;

      const response = await axios.post(`${API}/generate`, {
        notes, history_id: null,
        imaging_type: validImagingOrders.length > 0 ? validImagingOrders.map(i => `${i.type}${i.bodyPart ? ` (${i.bodyPart})` : ''}`).join(', ') : null,
        imaging_types: validImagingOrders.length > 0 ? validImagingOrders.map(i => i.type) : null,
        imaging_orders_detail: validImagingOrders.length > 0 ? validImagingOrders : null,
        specialty: validSpecialties.length > 0 ? validSpecialties.join(', ') : null,
        specialties: validSpecialties.length > 0 ? validSpecialties : null,
        patient_age: patientAge ? parseInt(patientAge) : null, patient_gender: patientGender || null,
        patient_status: patientStatus || null, chief_complaint: chiefComplaint || null,
        patient_label: patientLabel || null, patient_name: patientFullName, patient_dob: patientDobFormatted,
        icd10_code: manualIcd10 || null,
        icd10_order: (() => {
          const liveCodes = liveIcd10Suggestions.length > 0 ? liveIcd10Suggestions.map(s => s.code) : [];
          const manualCodes = manualIcd10 ? manualIcd10.split(',').map(c => c.trim()).filter(c => c && !liveCodes.includes(c)) : [];
          const allCodes = [...liveCodes, ...manualCodes];
          // Auto-append Z71.89 as LAST ICD-10 when deferred topics has content
          if (effectiveDeferredTopics && effectiveDeferredTopics.trim() && !allCodes.includes('Z71.89')) {
            allCodes.push('Z71.89');
          }
          return allCodes.length > 0 ? allCodes.join(', ') : null;
        })(),
        generate_types: generateTypes, letter_type: letterType || null, letter_tone: effectiveLetterTone || 'professional',
        letter_context: letterContext || null, char_limit: parseInt(charLimit) || 500,
        encounter_type: encounterType || 'established', visit_type: visitType || 'in_person_exam',
        include_ros: includeROS, include_pe: includePE, pe_unremarkable: peUnremarkable,
        medic_mode: !!medicMode,
        prior_visit_context: priorVisitContext || '',
        prep_notes: prepNotes || '',
        pe_exceptions: peExceptions || '',
        // Feb 22, 2026 (HIPAA-critical fix from test iter 109): NEVER send
        // provider identity fields in patient-mode requests. Previously these
        // fields were sent unconditionally — on a shared device where a
        // provider had ever logged in, patient-mode requests carried the
        // provider's identity and the backend dutifully signed the letter
        // with it. Reproduced by Lori's medic who saw Lori's name auto-fill
        // on her patient-side letters.
        signature_block: effectiveRole === 'patient' ? null : (user?.signature_block || profileSignature || null),
        provider_name: effectiveRole === 'patient' ? null : (user?.name || null),
        provider_mtf: effectiveRole === 'patient' ? null : (user?.default_mtf || profileMTF || null),
        provider_rank: effectiveRole === 'patient' ? null : (user?.default_rank || profileRank || null),
        occupation: occupation || null, job_duties: jobDuties || null,
        is_patient_mode: effectiveRole === 'patient', include_patient_quotes: includePatientQuotes || 'none',
        include_em_analysis: includeEmAnalysis,
        ap_format: apFormat || 'combined',
        clinical_tone: clinicalTone || 'empathetic',
        // Mar 3, 2026 — PE Unremarkable CV descriptor preference. Stored
        // on the user profile; localStorage is the source-of-truth mirror.
        // Defaults to observational "hemodynamically_stable" per DoD safety
        // posture; providers whose MTF prefers the classic "RRR, no murmurs"
        // can flip it in Settings.
        pe_cv_language: (() => {
          try {
            const v = localStorage.getItem('writeright_pe_cv_language');
            return v === 'rrr_no_murmurs' ? 'rrr_no_murmurs' : 'hemodynamically_stable';
          } catch { return 'hemodynamically_stable'; }
        })(),
        use_genesis_dot_phrases: (() => { try { return localStorage.getItem('writeright_use_genesis_dot_phrases') === 'true'; } catch { return false; } })(),
        // Feb 28, 2026 — Format-Lock on A/P (regen path). When the user has
        // already generated once AND has Format-Lock ON, send the CURRENT
        // (potentially edited) clinical note back as prior_output_edits and
        // turn on the server-side FORMAT LOCK directive so the LLM does
        // not reorder / re-indent / merge or split A/P bullets.
        prior_output_edits: (currentClinicalNote && currentClinicalNote.trim()) ? currentClinicalNote : null,
        // Legacy A/P-only flag kept for backwards compat with older backends.
        format_lock_ap: !!formatLockAp && !!(currentClinicalNote && currentClinicalNote.trim()),
        // Feb 28, 2026 v2 — per-section locks. Map {hpi, ros, pe, ap, edu,
        // mdm} → bool. Backend honors only when prior_output_edits is
        // non-empty (regen path).
        format_locks: (() => {
          try {
            const raw = localStorage.getItem('writeright_format_locks');
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && typeof parsed === 'object') return parsed;
            }
            // Fall back to legacy single-A/P flag.
            if (localStorage.getItem('writeright_format_lock_ap') === 'true') {
              return { ap: true };
            }
          } catch { /* ignore */ }
          return {};
        })(),
        document_mode: effectiveDocMode,
        message_scenario: messageScenario || null,
        guest_id: guestId,
        language: language || 'en',
        spanish_input: language === 'es',
        deferred_topics: effectiveDeferredTopics?.trim() || null,
        lab_orders: labOrders?.trim() || null,
        meds_rxd: medsRxd || false,
        // Feb 28, 2026 — structured Rx list string built from chips.
        // Forwarded to the LLM as context (e.g. "Ibuprofen • 600 mg q6h,
        // Pantoprazole • 40 mg qd") so the model knows the exact Rx
        // prescribed rather than inferring from dictation alone.
        meds_rxd_detail: (medsRxdDetail && medsRxdDetail.trim()) ? medsRxdDetail.trim() : null,
        // HIPAA scrubber: pass any terms the chip flagged on the previous
        // draft so the LLM is explicitly told NOT to repeat them. Consumed
        // once per call (cleared below).
        forbid_terms: forbidTermsRef.current && forbidTermsRef.current.length > 0
          ? forbidTermsRef.current
          : null,
      }, {
        withCredentials: true,
        // Feb 28, 2026 — Idempotency-Key. A UUID per generation attempt so
        // a queue replay (or accidental double-tap of Generate) returns
        // the cached response instead of running the LLM twice. The key
        // is randomized PER ATTEMPT so a legitimate "regenerate" still
        // hits the LLM fresh.
        headers: {
          'X-Idempotency-Key': (typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `gen_${Date.now()}_${Math.random().toString(36).slice(2)}`),
        },
      });
      // One-shot: clear the forbid list after this generation call.
      forbidTermsRef.current = null;

      // Locked pinned codes — mirror Speed Mode behavior (Feb 16, 2026).
      // The LLM's icd10_suggestions sometimes drops or rearranges codes the
      // provider explicitly typed into the manualIcd10 textbox. We re-inject
      // any user-typed code that the LLM omitted, so the displayed list is
      // always a superset of what the user pinned.
      const merged = { ...response.data };
      try {
        if (manualIcd10 && typeof manualIcd10 === 'string') {
          const userCodes = manualIcd10
            .split(/[,\n;]+/)
            .map((s) => s.trim().toUpperCase())
            .filter(Boolean);
          if (userCodes.length > 0) {
            const sugs = Array.isArray(merged.icd10_suggestions) ? [...merged.icd10_suggestions] : [];
            const sugSet = new Set(sugs.map((s) => (s.code || '').toUpperCase()));
            for (const code of userCodes) {
              if (!sugSet.has(code)) {
                sugs.push({
                  code,
                  description: _PINNED_CODE_DESC_FALLBACK[code] || '',
                  relevance: 'user_pinned',
                });
              }
            }
            merged.icd10_suggestions = sugs;
          }
        }
      } catch (e) {
        console.warn('[icd10 pin merge] failed:', e?.message);
      }
      setResult(merged);
      if (response.data.history_id) {
        // Caller can use this via returned result
      }

      // Patient cover letter
      if (includeCoverLetter && response.data.admin_letter) {
        const fn = patientFullName || 'Patient';
        const cover = `COVER LETTER\n\nDear Provider,\n\nI am submitting this draft letter for your review and consideration. I created this template using WriteRight PCM+ to help organize the information you may need to complete my request.\n\nThis is NOT a final document - it is a draft that requires your professional review, modifications, and signature. I understand that:\n\n\u2022 You may need to verify or update the clinical information\n\u2022 Additional appointments may be required to re-establish my care\n\u2022 The final decision and wording is entirely at your discretion\n\u2022 This template is meant to save time, not replace your medical judgment\n\nI am prepared to actively participate in my care plan and follow through on any referrals, imaging, or treatments you recommend.\n\nThank you for your time and consideration.\n\nRespectfully,\n${fn}\n\n---\n\nDRAFT LETTER FOR PROVIDER REVIEW:\n\n`;
        setResult(prev => ({ ...prev, admin_letter: cover + prev.admin_letter }));
      }

      setLastShareUrl(null);
      setDocumentsReady(true);
      setGenerationVersion(prev => prev + 1);

      // Auto-mark drafts as completed
      if (onGenerationComplete) onGenerationComplete(response.data);

      // Auto-share to team
      // Feb 28, 2026 — if global auto-stream is ON, broadcast to ALL the
      // user's assigned teams (per user direction: "All teams I'm assigned
      // to"). Otherwise fall back to legacy per-team selection.
      if (response.data.history_id) {
        const broadcastTargets = (globalAutoStreamOn && Array.isArray(userTeams) && userTeams.length > 0)
          ? userTeams.map(t => t.id)
          : (autoShareTeamId ? [autoShareTeamId] : []);
        if (broadcastTargets.length > 0) {
          try {
            await Promise.all(broadcastTargets.map(tid =>
              axios.post(`${API}/teams/${tid}/share-note`, { note_id: response.data.history_id }, { withCredentials: true })
                .catch(e => console.error('Auto-share fail', tid, e))
            ));
            toast.success(
              broadcastTargets.length === 1
                ? 'Auto-shared to team'
                : `Auto-shared to ${broadcastTargets.length} teams`,
              { duration: 2000 }
            );
          } catch (e) {
            console.error('Auto-share failed:', e);
            toast.error('Auto-share failed — check team settings');
          }
        }
      }

      // Auto-fill known bracket fields from form state before showing modal
      if (response.data.admin_letter) {
        const formState = getFormStateForFields();
        const fullName = [formState.soldierRank, formState.soldierFirstName, formState.soldierLastName].filter(Boolean).join(' ').trim();
        const dobFormatted = formState.soldierDob ? new Date(formState.soldierDob).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
        const autoFill = {};
        // Feb 18, 2026: seed autoFill with previously-answered bracketFields so
        // "Refresh Letter" / any regenerate reuses cached answers (e.g. the
        // date the provider already typed) without re-prompting.
        try {
          if (bracketFields && typeof bracketFields === 'object') {
            for (const [k, v] of Object.entries(bracketFields)) {
              if (v && String(v).trim()) autoFill[k] = v;
            }
          }
        } catch (e) { /* non-fatal */ }
        if (fullName) {
          autoFill['Patient Name'] = fullName;
          autoFill['PATIENT NAME'] = fullName;
          autoFill['Patient Full Name'] = fullName;
          autoFill['PATIENT FULL NAME'] = fullName;
          autoFill['patient name'] = fullName;
          autoFill['Name'] = fullName;
        }
        if (dobFormatted) {
          autoFill['Date of Birth'] = dobFormatted;
          autoFill['DATE OF BIRTH'] = dobFormatted;
          autoFill['DOB'] = dobFormatted;
          autoFill['Date'] = dobFormatted;
          autoFill['date of birth'] = dobFormatted;
        }
        if (formState.soldierUnit) {
          autoFill['Unit'] = formState.soldierUnit;
          autoFill['UNIT'] = formState.soldierUnit;
        }
        if (formState.soldierPhone) {
          autoFill['Phone'] = formState.soldierPhone;
          autoFill['Phone Number'] = formState.soldierPhone;
        }
        if (Object.keys(autoFill).length > 0) {
          response.data.admin_letter = applyFieldValues(response.data.admin_letter, autoFill);
          // Update result state with auto-filled letter
          setResult(prev => ({ ...prev, admin_letter: response.data.admin_letter }));
        }
        // Check for remaining unfilled brackets
        const missing = extractBracketFields(response.data.admin_letter, formState);
        if (missing.length > 0) {
          const init = {};
          missing.forEach(f => init[f] = '');
          setBracketFields(init);
          setShowBracketModal(true);
          setPendingGeneration(true);
        }
      }

      // Set initial output tab
      if (effectiveRole === 'patient') {
        if (response.data.visit_prep_script && (documentMode === 'script' || letterType === 'visit_prep_script')) setActiveOutputTab('visit_prep_script');
        else if (response.data.portal_message && (documentMode === 'message')) setActiveOutputTab('portal_message');
        else if (response.data.admin_letter) setActiveOutputTab('admin_letter');
        else if (response.data.visit_prep_script) setActiveOutputTab('visit_prep_script');
        else if (response.data.portal_message) setActiveOutputTab('portal_message');
      } else {
        if (response.data.clinical_note) setActiveOutputTab('clinical_note');
        else if (response.data.referrals?.length > 1) setActiveOutputTab('referral-0');
        else if (response.data.indication) setActiveOutputTab('indication');
        else if (response.data.admin_letter) setActiveOutputTab('admin_letter');
        else if (response.data.limitations) setActiveOutputTab('limitations');
      }

      toast.success('Documents generated!', { duration: 5000, style: { background: '#22c55e', color: 'white', fontWeight: 'bold', fontSize: '16px' } });

      // ── Append-to-Profile nudge (v6.8.47) ────────────────────────────
      // After a disability/appeal letter generates and the user's notes
      // contain new content that doesn't appear in any of their already-
      // saved injuries, surface a follow-up toast inviting them to add
      // it to their medical history so future letters get the same
      // auto-injection. We only fire for the 5 disability-letter
      // families where saved_injuries auto-inject is active.
      try {
        const DISABILITY_LETTER_TYPES = new Set([
          'ssa_disability_statement',
          'ltd_application_statement',
          'ides_appeal_soldier',
          'buddy_statement',
          'joes_survey_response',
        ]);
        if (effectiveRole === 'patient' && DISABILITY_LETTER_TYPES.has(letterType) && (notes || '').trim().length > 30) {
          const lower = (notes || '').toLowerCase();
          const savedLabelsLower = (savedInjuries || []).map(i => (i?.label || '').toLowerCase().trim()).filter(Boolean);
          const alreadyKnown = savedLabelsLower.some(l => l && lower.includes(l));
          if (!alreadyKnown) {
            // Stagger by 1.5 s so the "Documents generated!" toast lands
            // first; the nudge appears just after.
            setTimeout(() => {
              toast.info(
                'Save your current situation to your medical history? It\'ll auto-inject into future disability letters.',
                {
                  duration: 11000,
                  action: {
                    label: 'Save to Profile',
                    onClick: () => {
                      try {
                        const firstLine = (notes || '').split(/[\n.]/).map(s => s.trim()).filter(Boolean)[0] || '';
                        window.dispatchEvent(new CustomEvent('writeright:open-profile-with-injury-draft', {
                          detail: { draftLabel: firstLine.slice(0, 140) },
                        }));
                      } catch { /* ignore */ }
                    },
                  },
                  style: { background: '#0ea5e9', color: 'white', fontWeight: 600 },
                }
              );
            }, 1500);
          }
        }
      } catch (_e) { /* never block on the nudge */ }
      
      // Auto-scroll to output panel — Feb 24, 2026 fix: previously only fired
      // in patient mode; provider workflow felt choppy because after Generate
      // the view stayed on the input area and the provider had to manually
      // hunt for the output. Now scrolls in BOTH modes. The actual scroll
      // target differs: patient has `#patient-output-section`, provider has
      // `#output-section`.
      setTimeout(() => {
        const outputEl =
          document.getElementById('patient-output-section') ||
          document.getElementById('output-section');
        if (outputEl) outputEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 450);
      
      // Auto-copy clinical note to clipboard (Win+V clipboard history safety net)
      // Only auto-copies on FIRST generation (new patient) — regenerations skip to preserve Win+V slots
      const isFirstGeneration = !result;
      if (response.data.clinical_note && isFirstGeneration) {
        try {
          const autoCopyParts = [];
          // Build the note title
          const age = patientAge ? `${patientAge}yo` : '';
          const gender = patientGender === 'male' ? 'M' : patientGender === 'female' ? 'F' : '';
          const status = patientStatus ? patientStatus.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : '';
          const enc = encounterType === 'new' ? 'New' : 'Est';
          const titleParts = [enc, age, gender, status, chiefComplaint].filter(Boolean);
          if (titleParts.length > 0) autoCopyParts.push(titleParts.join(' '));
          autoCopyParts.push(response.data.clinical_note);
          await navigator.clipboard.writeText(autoCopyParts.join('\n\n'));
          toast.info('Note auto-copied to clipboard', { duration: 2000, icon: '📋' });
        } catch (e) {
          // Clipboard API may fail if page not focused — silent fail is fine
          console.warn('Auto-copy to clipboard failed:', e);
        }
      }

      // Cache to offline history (localStorage safety net)
      // Deduplicates by history_id — regenerating a note REPLACES the cached version
      try {
        const cacheEntry = {
          id: response.data.history_id || `local-${Date.now()}`,
          clinical_note: response.data.clinical_note,
          admin_letter: response.data.admin_letter,
          icd10_suggestions: response.data.icd10_suggestions,
          chief_complaint: chiefComplaint,
          patient_age: patientAge,
          patient_gender: patientGender,
          patient_status: patientStatus,
          encounter_type: encounterType,
          timestamp: new Date().toISOString(),
        };
        const cached = JSON.parse(localStorage.getItem('writeright_offline_history') || '[]');
        // Remove any existing entry for the same history_id (version update)
        const deduplicated = cached.filter(e => e.id !== cacheEntry.id);
        deduplicated.unshift(cacheEntry);
        // Keep last 25 unique encounters
        localStorage.setItem('writeright_offline_history', JSON.stringify(deduplicated.slice(0, 25)));
      } catch (e) {
        console.warn('Offline cache save failed:', e);
      }

      loadHistory();
      if (shareCode && updateShareSession) {
        updateShareSession(response.data);
        toast.info('Shared session updated!', { duration: 3000 });
      }

      return response.data;
    } catch (error) {
      console.error('Generation error:', error);
      // Mar 2, 2026 — richer failure message so the provider can tell us
      // WHAT failed (network vs 4xx vs 5xx vs timeout) instead of the
      // generic "Generation failed." which hid the root cause.
      const calledUrl = `${window.__WR_API_BASE__ || ''}/api/generate`;
      let msg = 'Generation failed.';
      if (error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')) {
        msg = 'Generation timed out — try fewer outputs or shorter notes.';
      } else if (error?.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail;
        if (status === 403) {
          msg = `Generation blocked (403) at ${calledUrl}. Likely wrong URL / firewall — bookmark chart-fast.preview.emergentagent.com or pcm-write-time-1.emergent.host.`;
        } else if (typeof detail === 'string') {
          msg = `Generation failed (${status}): ${detail}`;
        } else if (Array.isArray(detail) && detail[0]?.msg) {
          // Pydantic validation error shape
          msg = `Generation failed (${status}): ${detail[0].msg} at ${(detail[0].loc || []).join('.')}`;
        } else {
          msg = `Generation failed (HTTP ${status}) at ${calledUrl}. Check console.`;
        }
      } else if (error?.request) {
        msg = 'Generation failed — no server response. Check network / VPN.';
      }
      toast.error(msg, { duration: 10000 });
      return null;
    } finally {
      setIsGenerating(false);
    }
  }, [notes, generateIndication, generateClinicalNote, generateAdminLetter, generateLimitations,
      specialties, imagingOrders, currentDraftId, saveDraft, setIsGenerating,
      soldierRank, soldierFirstName, soldierLastName, soldierDob,
      patientAge, patientGender, patientStatus, chiefComplaint, patientLabel,
      manualIcd10, liveIcd10Suggestions, letterType, effectiveLetterTone,
      letterContext, charLimit, encounterType, visitType, includeROS, includePE, peUnremarkable,
      user, profileSignature, profileMTF, profileRank, occupation, jobDuties,
      effectiveRole, includePatientQuotes, includeEmAnalysis, guestId,
      includeCoverLetter, autoShareTeamId, shareCode, updateShareSession,
      globalAutoStreamOn, userTeams,
      language,
      deferredTopics, documentMode, messageScenario,
      setResult, setLastShareUrl, setDocumentsReady, setGenerationVersion, setActiveOutputTab,
      getFormStateForFields, setBracketFields, setShowBracketModal, setPendingGeneration, loadHistory,
      bracketFields]);

  const checkNotesForMissingFields = useCallback(() => {
    const missing = extractBracketFields(notes, getFormStateForFields());
    if (missing.length > 0) {
      const init = {};
      missing.forEach(f => init[f] = '');
      setPreGenFields(init);
      setShowPreGenModal(true);
      return true;
    }
    return false;
  }, [notes, getFormStateForFields, setPreGenFields, setShowPreGenModal]);

  // Feb 26, 2026 — Pre-generate conflict scanner. Surfaces pronoun mix and
  // endorse/denies contradictions so the provider can fix them BEFORE the
  // LLM call. Returns true if conflicts were detected (and modal was opened).
  // The `bypassConflictRef` lets the user choose "Generate Anyway" without
  // re-tripping the same scan on the second pass.
  const bypassConflictRef = useRef(false);
  const checkNotesForConflicts = useCallback(() => {
    if (bypassConflictRef.current) {
      bypassConflictRef.current = false;
      return false;
    }
    const found = scanNotesForConflicts(notes);
    if (found.length > 0) {
      if (setConflicts) setConflicts(found);
      if (setShowConflictModal) setShowConflictModal(true);
      return true;
    }
    return false;
  }, [notes, setConflicts, setShowConflictModal]);

  const generateDocuments = useCallback(async () => {
    if (!notes.trim()) { toast.error('Please enter or record some notes first'); return; }
    trackFeature('generate_docs');
    // Mar 1, 2026 — Soft reminder when the provider hasn't explicitly picked
    // a visit type. We auto-fall-back to "in_person_exam" downstream so the
    // note still generates, but providers frequently forget that the default
    // ISN'T Virtual (or vice-versa). This toast nudges WITHOUT blocking.
    // Dismissable with the "Skip Reminder" key set in localStorage.
    //
    // BACK-CHARTING ESCALATION — when dosOverride is set and != today, the
    // visit-type auto-fill from the schedule is almost certainly wrong (the
    // provider is reconstructing a past encounter that had its OWN type).
    // We always fire the toast in that case, even if visitType is non-empty,
    // because the SCHEDULE-derived default can land on a value the provider
    // never intended. Stronger language + 8s duration + non-dismissable.
    try {
      const today = new Date().toISOString().slice(0, 10);
      const isBackCharting = !!dosOverride && dosOverride !== today;
      const skip = localStorage.getItem('writeright_skip_visit_type_reminder') === '1';
      if (isBackCharting) {
        toast.warning(
          `Back-charting ${dosOverride} — DOUBLE-CHECK the visit type (currently: ${visitType || 'not set'}). Schedule auto-fill is for TODAY only.`,
          { duration: 8000, id: 'visit-type-backchart-reminder' }
        );
      } else if (!skip && (!visitType || visitType === '')) {
        toast.warning(
          'Visit type not set — defaulting to In-Person. Tap In-Person / Virtual / Counseling / Refill in the demographic bar to override.',
          { duration: 5000, id: 'visit-type-reminder' }
        );
      }
    } catch (_) { /* ignore localStorage failures */ }

    // Mar 4, 2026 (v6.9.5) — Voice-first workflow: NO mandatory demographic
    // confirm() modal. Provider explicitly requested removal of the block —
    // the auto-detect hook (useAutoDetect) already parses "25 year old
    // female" / "25 F" / "female age 32" from voice/dictation and populates
    // the Age & Sex fields before this guard runs. If auto-detect misses
    // (rare edge cases), the backend now emits generic "patient" phrasing
    // with "they/them" pronouns rather than fabricating demographics, and
    // the enforce_gender_pronouns / enforce_patient_age scrubbers catch
    // any residual bad output. Net: no more workflow-breaking modal.

    const generateTypes = [];
    if (generateIndication) generateTypes.push('indication');
    if (generateClinicalNote) generateTypes.push('clinical_note');
    if (generateAdminLetter && letterType) generateTypes.push('admin_letter');
    if (generateLimitations) generateTypes.push('limitations');
    const effectiveDocMode = documentMode || 'letter';
    if (effectiveDocMode === 'message' || effectiveDocMode === 'both') {
      generateTypes.push('portal_message');
    }
    if (generateTypes.length === 0) { toast.error('Please select at least one output type'); return; }
    if (checkNotesForMissingFields()) return;
    if (checkNotesForConflicts()) return;
    executeGeneration();
  }, [notes, generateIndication, generateClinicalNote, generateAdminLetter, letterType,
      generateLimitations, documentMode, checkNotesForMissingFields, checkNotesForConflicts, executeGeneration, trackFeature,
      dosOverride, visitType, effectiveRole, patientAge, patientGender]);

  // User reviewed conflicts and chose to proceed anyway.
  const proceedAfterConflict = useCallback(() => {
    if (setShowConflictModal) setShowConflictModal(false);
    if (setConflicts) setConflicts([]);
    bypassConflictRef.current = true;
    // Re-run the generate pipeline; the bypass flag will short-circuit the scan.
    generateDocuments();
  }, [setShowConflictModal, setConflicts, generateDocuments]);

  const dismissConflictModal = useCallback(() => {
    if (setShowConflictModal) setShowConflictModal(false);
    if (setConflicts) setConflicts([]);
    // Try to focus the notes textarea so the user can immediately edit.
    setTimeout(() => {
      try {
        const el = document.querySelector('[data-testid="notes-textarea"]');
        if (el && typeof el.focus === 'function') el.focus();
      } catch (e) { /* ignore */ }
    }, 60);
  }, [setShowConflictModal, setConflicts]);

  /**
   * applyConflictFix — one-tap fix from inside the conflict modal.
   * Rewrites `notes` and re-scans. If all conflicts cleared, auto-proceeds
   * to generate. Otherwise keeps the modal open with remaining conflicts.
   */
  const applyConflictFix = useCallback((conflictId, action) => {
    if (!action || !action.kind) return;
    let nextNotes = notes;
    if (action.kind === 'pronoun_fix') {
      nextNotes = applyPronounFix(notes, action.payload);
      // Feb 28, 2026 — CRITICAL FIX (gender pronoun regression):
      // When the provider taps "Patient is Male/Female" inside the conflict
      // modal, ALSO update the patientGender form state. Previously we only
      // rewrote the notes text but the backend `patient_gender` payload field
      // remained empty, so the post-LLM `enforce_gender_pronouns` scrub fell
      // through to its unreliable auto-detect branch and the wrong-gender
      // pronouns survived in the generated note.
      if (typeof setPatientGender === 'function' && action.payload) {
        setPatientGender(action.payload);
      }
    } else if (action.kind === 'endorse_denies_fix') {
      nextNotes = applyEndorseDeniesFix(notes, action.payload);
    }
    if (nextNotes !== notes) setNotes(nextNotes);

    // Re-scan with the updated text to see what's left.
    const remaining = scanNotesForConflicts(nextNotes);
    if (setConflicts) setConflicts(remaining);
    if (remaining.length === 0) {
      // All clear — close modal and proceed to generate.
      if (setShowConflictModal) setShowConflictModal(false);
      bypassConflictRef.current = true;
      // Defer one tick so the notes state update is committed before generate
      setTimeout(() => { generateDocuments(); }, 30);
      toast.success('Conflict resolved — generating.');
    } else {
      toast.success('Fix applied. Review remaining items.');
    }
  }, [notes, setNotes, setShowConflictModal, setConflicts, setPatientGender, generateDocuments]);

  const handlePreGenFieldsComplete = useCallback(() => {
    setShowPreGenModal(false);
    setNotes(applyFieldValues(notes, preGenFields));
    setPreGenFields({});
    executeGeneration();
  }, [notes, preGenFields, setNotes, setShowPreGenModal, setPreGenFields, executeGeneration]);

  const skipPreGenAndGenerate = useCallback(() => {
    setShowPreGenModal(false);
    setPreGenFields({});
    executeGeneration();
  }, [setShowPreGenModal, setPreGenFields, executeGeneration]);

  // ICD-10 reorder handlers
  const handleIcd10Reorder = useCallback((newOrder) => {
    setManualIcd10Order(newOrder);
    if (result && !isGenerating) setIcd10PendingConfirm(true);
  }, [result, isGenerating, setManualIcd10Order]);

  const handleIcd10Delete = useCallback((codeToDelete) => {
    const current = liveIcd10Suggestions || [];
    const filtered = current.filter(c => c.code !== codeToDelete);
    setManualIcd10Order(filtered);
    if (result && !isGenerating) setIcd10PendingConfirm(true);
  }, [liveIcd10Suggestions, result, isGenerating, setManualIcd10Order]);

  const confirmIcd10Sequence = useCallback(() => {
    setIcd10PendingConfirm(false);
    generateDocuments();
  }, [generateDocuments]);

  const dismissIcd10Confirm = useCallback(() => {
    setIcd10PendingConfirm(false);
  }, []);

  // Auto-regenerate admin letter when tone changes
  const prevLetterToneRef = useRef(effectiveLetterTone);
  useEffect(() => {
    if (prevLetterToneRef.current !== effectiveLetterTone && result?.admin_letter && !isGenerating && notes.trim()) {
      prevLetterToneRef.current = effectiveLetterTone;
      toast.info(`Regenerating letter with ${effectiveLetterTone} tone...`);
      executeGeneration();
    } else {
      prevLetterToneRef.current = effectiveLetterTone;
    }
  }, [effectiveLetterTone]); // eslint-disable-line react-hooks/exhaustive-deps

  // Feb 18, 2026: "Refresh Letter Only" — regenerate just the admin letter
  // without re-prompting for already-answered bracket fields (date, etc.).
  // Cached bracketFields are auto-applied inside executeGeneration's autoFill
  // step, so the user does not have to retype the date on every refresh.
  const refreshAdminLetterOnly = useCallback(async () => {
    if (!notes.trim()) { toast.error('No notes to regenerate from'); return; }
    if (!letterType) { toast.error('Pick a letter type before refreshing'); return; }
    if (isGenerating) return;
    trackFeature('refresh_letter_only');
    toast.info('Refreshing letter…', { duration: 1500 });
    await executeGeneration();
  }, [notes, letterType, isGenerating, executeGeneration, trackFeature]);

  // Mar 2, 2026 — TRUE referral-only regenerate. Calls `/api/generate` with
  // `generate_types: ['indication']` explicitly (NOT the current flags) so
  // the LLM only produces the referral indication + per-specialty referrals.
  // MERGES the response into the existing `result` state instead of
  // overwriting — so the clinical note the provider already reviewed stays
  // exactly as-is, and only the referral fields get filled in. Provider
  // asked for this: "referrals get added — the existing note gets
  // re-generated with the same input" (i.e., stop re-generating the note).
  const refreshReferralOnly = useCallback(async (specialtiesOverride, opts = {}) => {
    if (!notes.trim()) { toast.error('No notes to add a referral from'); return; }
    if (isGenerating) return;
    const { appendToExisting = false } = opts;
    const specsToUse = (specialtiesOverride && specialtiesOverride.length > 0)
      ? specialtiesOverride
      : (Array.isArray(specialties) && specialties.length > 0 ? specialties : []);
    if (specsToUse.length === 0) {
      toast.error('Add at least one specialty before generating a referral');
      return;
    }
    trackFeature('refresh_referral_only');
    toast.info('Generating referral…', { duration: 1800 });
    setIsGenerating(true);
    try {
      const payload = {
        notes,
        history_id: result?.history_id,
        specialties: specsToUse,
        patient_age: patientAge || undefined,
        patient_gender: patientGender || undefined,
        patient_status: patientStatus || undefined,
        icd10_order: primaryIcd10Codes || manualIcd10 || undefined,
        char_limit: parseInt(charLimit) || 500,
        generate_types: ['indication'],
      };
      const response = await axios.post(`${API}/generate`, payload, {
        withCredentials: true,
        timeout: 120000,
      });
      const r = response.data || {};
      // MERGE into existing result — keep clinical_note/letter/etc untouched.
      // When `appendToExisting` is set, we tack new referrals onto the end
      // rather than replacing the array. Used by "+ Add Another Referral"
      // so providers can request multiple specialty consults incrementally.
      setResult((prev) => {
        const prevReferrals = (prev && Array.isArray(prev.referrals)) ? prev.referrals : [];
        const prevSpecs = (prev && Array.isArray(prev.specialties)) ? prev.specialties : [];
        const nextReferrals = appendToExisting
          ? [...prevReferrals, ...((r.referrals) || [])]
          : (r.referrals ?? prevReferrals);
        const nextSpecs = appendToExisting
          ? Array.from(new Set([...prevSpecs, ...((r.specialties) || specsToUse)]))
          : (r.specialties ?? prevSpecs);
        return {
          ...(prev || {}),
          indication: appendToExisting ? (prev?.indication || r.indication) : (r.indication ?? prev?.indication),
          indication_char_count: appendToExisting ? prev?.indication_char_count : (r.indication_char_count ?? prev?.indication_char_count),
          referrals: nextReferrals,
          specialties: nextSpecs,
          history_id: r.history_id || prev?.history_id,
        };
      });
      setActiveOutputTab && setActiveOutputTab('indication');
      toast.success(appendToExisting ? `Added referral for ${specsToUse.join(', ')}` : 'Referral added — existing note preserved', { duration: 2500 });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Referral generation failed';
      toast.error(msg);
    } finally {
      setIsGenerating(false);
    }
  }, [notes, isGenerating, result, specialties, patientAge, patientGender, patientStatus, primaryIcd10Codes, manualIcd10, charLimit, setResult, setActiveOutputTab, setIsGenerating, trackFeature]);

  // Feb 18, 2026: "Scrub & Refresh" — when the HIPAA Review chip catches a
  // diagnosis leak, this callback regenerates the letter with the flagged
  // terms passed to the backend as forbid_terms. The new system-prompt
  // injection (server.py) tells the LLM "DO NOT repeat these exact words".
  const scrubAndRefreshLetter = useCallback(async (flaggedTerms = []) => {
    if (!notes.trim()) { toast.error('No notes to regenerate from'); return; }
    if (!letterType) { toast.error('Pick a letter type before scrubbing'); return; }
    if (isGenerating) return;
    const terms = (flaggedTerms || []).filter(Boolean);
    if (terms.length === 0) { toast.error('No terms to scrub'); return; }
    forbidTermsRef.current = terms;
    trackFeature('scrub_and_refresh_letter');
    toast.info(
      terms.length === 1
        ? `Scrubbing "${terms[0]}" and regenerating…`
        : `Scrubbing ${terms.length} flagged terms and regenerating…`,
      { duration: 2500 }
    );
    await executeGeneration();
  }, [notes, letterType, isGenerating, executeGeneration, trackFeature]);

  // Feb 27, 2026 — Expose a reset for the bypass flag. The flag is normally
  // self-clearing on the next scan, but if the user picks up a Baton or loads
  // from history *after* clicking "Generate Anyway" on a previous patient and
  // before re-generating, the stale `true` would silently bypass conflict
  // scanning on the newly loaded payload. Callers (loadHistoryItem, baton
  // pickup) call this to keep the next generate honest.
  const resetBypassConflict = useCallback(() => {
    bypassConflictRef.current = false;
  }, []);

  return {
    generateDocuments, executeGeneration,
    handleBracketFieldsComplete,
    checkNotesForMissingFields,
    checkNotesForConflicts, proceedAfterConflict, dismissConflictModal, applyConflictFix,
    handlePreGenFieldsComplete, skipPreGenAndGenerate,
    handleIcd10Reorder, handleIcd10Delete,
    icd10PendingConfirm, confirmIcd10Sequence, dismissIcd10Confirm,
    getFormStateForFields,
    refreshAdminLetterOnly,
    refreshReferralOnly,
    scrubAndRefreshLetter,
    resetBypassConflict,
  };
};

export default useGeneration;
